export * from "./quality";
export * from "./market-verification";
import { MarketEvidenceSubmissionSchema, MarketVerificationRecordSchema } from "./market-verification";
export * from "./evidence";
export * from "./evidence-compact";
export * from "./extraction-expression";
export * from "./evidence-acquisition";
export * from "./evidence-calculation";
export * from "./screening-conclusion";
import { MarketEvidenceSubmissionV2Schema, MarketVerificationRecordV2Schema } from "./market-verification";
export * from "./search-localization";
import { SearchLocalizationSchema } from "./search-localization";
export * from "./products";
export * from "./public-url";
export * from "./review-band";
export * from "./goal-task";
import { GoalTaskSchema } from "./goal-task";
export * from "./execution-recipe";
export * from "./failure-taxonomy";
export * from "./selection-conclusion";
import { isolateProductFailures, MAX_PAGE_PRODUCTS, PageProductSchema, type ProductCards } from "./products";
import { PublicNavigationUrlSchema } from "./public-url";
export * from "./context-evidence";
export * from "./growth";
export * from "./growth-outcomes";
export * from "./task-fields";
export * from "./task-command";
export * from "./profile-fields";
export * from "./operations";
export * from "./image-understanding";
export * from "./product-visual-details";
import { SourceVisualDetailsSchema } from "./product-visual-details";
import type { ImageAnalysisEventData } from "./image-understanding";
import { MAX_IMAGES_PER_RUN } from "./image-understanding";

import { z } from "zod";
import { CoachingScopeSchema } from "./coaching";
export * from "./coaching";
export * from "./local-coaching";
export * from "./privacy";

export const ModeSchema = z.enum(["fast", "balanced", "deep"]);
export type Mode = z.infer<typeof ModeSchema>;

export const ForcedToolSchema = z.enum(["search", "page", "vision", "form"]).optional();
export type ForcedTool = z.infer<typeof ForcedToolSchema>;

export const ElementSummarySchema = z.object({
  elementId: z.string(),
  tag: z.string(),
  role: z.string().optional(),
  type: z.string().optional(),
  label: z.string().optional(),
  text: z.string().optional(),
  placeholder: z.string().optional(),
  options: z.array(z.string()).optional(),
  required: z.boolean().optional(),
  disabled: z.boolean().optional(),
  nearbyText: z.string().optional(),
  // Normalized viewport geometry for matching screenshot regions to live DOM IDs.
  viewportBounds: z.object({ x: z.number().finite(), y: z.number().finite(), width: z.number().positive(), height: z.number().positive() }).optional(),
});
export type ElementSummary = z.infer<typeof ElementSummarySchema>;

export const FormSummarySchema = z.object({
  formId: z.string(),
  label: z.string().optional(),
  fields: z.array(ElementSummarySchema),
});
export type FormSummary = z.infer<typeof FormSummarySchema>;

export const PageContextSchema = z.object({
  url: z.string(),
  title: z.string(),
  domain: z.string(),
  language: z.string().optional(),
  visibleText: z.string().max(16000),
  pageType: z.string().optional(),
  interactiveElements: z.array(ElementSummarySchema).max(100),
  selectedElement: ElementSummarySchema.optional(),
  forms: z.array(FormSummarySchema).max(20).optional(),
  products: z.array(PageProductSchema).max(MAX_PAGE_PRODUCTS).optional(),
});
export type PageContext = z.infer<typeof PageContextSchema>;

/**
 * 整页上下文校验，但把**商品级**失败隔离出来。
 *
 * 以前调用方是 `PageContextSchema.parse(...)`：任一 `products[i].id` 不合法就抛错，
 * 连正文一起丢掉。现在单个商品不合格式只丢它自己，`droppedProducts` 由调用方显式处理。
 *
 * 边界很硬：**只有能归因于商品数组的失败才被隔离**。其它字段（url/visibleText/…）坏掉时
 * 仍然抛出原本的 ZodError —— 隔离不允许变成"悄悄放宽校验"。
 */
export function parsePageContextIsolated(input: unknown): { context: PageContext; droppedProducts: number } {
  const direct = PageContextSchema.safeParse(input);
  if (direct.success) return { context: direct.data, droppedProducts: 0 };
  const repaired = isolateProductFailures(input);
  if (!repaired) throw direct.error;
  const retry = PageContextSchema.safeParse(repaired.context);
  if (!retry.success) throw direct.error;
  return { context: retry.data, droppedProducts: repaired.dropped };
}

/**
 * 页面上下文的**最小摘要**：只回答"这是哪一页"，不携带页面内容。
 *
 * 数据面规则（第八域）：执行过程中收集到的正文、表单、选中元素与商品明细一律留在本地；
 * 服务端只允许持久化这个形状。所以这里刻意用 `.strict()`——多带 `visibleText` 之类的字段
 * 会直接解析失败，而不是被静默丢弃（静默丢弃会让"到底存没存"变成一个没人能验证的问题）。
 *
 * `visibleTextLength` 让人知道当时页面有多少内容、`textHash` 让人判断两次案例是不是同一现场，
 * 都不需要拿到内容本身。
 */
export const PageContextSummarySchema = z.object({
  url: z.string().max(1200),
  title: z.string().max(500),
  domain: z.string().max(240),
  pageType: z.string().max(80).optional(),
  visibleTextLength: z.number().int().nonnegative(),
  textHash: z.string().regex(/^[a-f\d]{16}$/),
}).strict();
export type PageContextSummary = z.infer<typeof PageContextSummarySchema>;

export * from "./support-screenshots";

export const ImageAttachmentSchema = z.object({
  dataUrl: z.string().startsWith("data:image/"),
  mimeType: z.enum(["image/png", "image/jpeg", "image/webp"]),
  name: z.string().max(180).optional(),
});
export type ImageAttachment = z.infer<typeof ImageAttachmentSchema>;

export { isPreviousReplyTransform, referencesPreviousReply } from "./reply-transform";
export { isFieldHelpQuestion, isFieldConversationFollowup, quoteFieldConversation } from "./field-question";
export const ConversationTurnSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string().min(1).max(4000),
});
export type ConversationTurn = z.infer<typeof ConversationTurnSchema>;

export const SellerProfileSchema = z.object({
  platforms: z.array(z.string().min(1).max(60)).max(12).default([]),
  markets: z.array(z.string().min(1).max(60)).max(12).default([]),
  countries: z.array(z.string().min(1).max(60)).max(24).default([]),
  categories: z.array(z.string().min(1).max(100)).max(20).default([]),
  experienceStage: z.enum(["preparing", "beginner", "operating", "scaling"]).optional(),
  businessType: z.enum(["individual", "sole_proprietor", "company"]).optional(),
});
export type SellerProfile = z.infer<typeof SellerProfileSchema>;

export const AnswerSourceSchema = z.object({
  title: z.string(),
  url: z.string().url().optional(),
  domain: z.string().optional(),
  type: z.enum(["current_page", "official", "knowledge", "case", "model_analysis"]),
  retrievedAt: z.string().optional(),
});
export type AnswerSource = z.infer<typeof AnswerSourceSchema>;

export const FormPlanFieldSchema = z.object({
  fieldId: z.string(),
  label: z.string(),
  currentValue: z.string().optional(),
  suggestedValue: z.string(),
  reason: z.string(),
  confidence: z.enum(["high", "medium", "low"]),
  warnings: z.array(z.string()).default([]),
});
export type FormPlanField = z.infer<typeof FormPlanFieldSchema>;

export const FormPlanSchema = z.object({
  locationAssisted: z.boolean().optional(),
  id: z.string(),
  taskId: z.string().max(120).optional(),
  summary: z.string(),
  fields: z.array(FormPlanFieldSchema),
  excludedSensitiveFields: z.array(z.string()).default([]),
  excludedManualFields: z.array(z.string()).default([]),
  pageSnapshot: z.object({
    url: z.string().max(1200),
    title: z.string().max(500),
    domain: z.string().max(240),
    fieldIds: z.array(z.string()).max(100),
  }).optional(),
  willSubmit: z.literal(false),
});
export type FormPlan = z.infer<typeof FormPlanSchema>;

export const FormApplicationRequestSchema = z.object({
  requireExactPage: z.boolean().optional(),
  planId: z.string().min(1),
  pageSnapshot: FormPlanSchema.shape.pageSnapshot.unwrap(),
  values: z.array(z.object({ fieldId: z.string(), value: z.string().max(8000) })).max(100),
});
export type FormApplicationRequest = z.infer<typeof FormApplicationRequestSchema>;

export const FormApplicationResultSchema = z.object({
  planId: z.string().min(1),
  status: z.enum(["verified", "partial", "stale"]),
  applied: z.array(z.string()).max(100),
  skipped: z.array(z.string()).max(100),
  mismatched: z.array(z.string()).max(100),
  pageUrl: z.string().max(1200),
  /**
   * `status: "stale"` 时**逐个字段说明原因**（哪个字段、为什么填不进去）。
   *
   * 真机 2026-09-23：点击"确认填入"后什么都没填，界面只说"页面或表单已变化"——
   * 而真实原因是 React 重渲染替换了控件节点。拒绝执行是对的，
   * 但"地址变了""控件找不回来""控件已不可填"是三种完全不同的处置，
   * 混成一句话就等于让用户（和排查的人）无路可走。
   */
  reasons: z.array(z.string().max(300)).max(100).optional(),
});
export type FormApplicationResult = z.infer<typeof FormApplicationResultSchema>;

export const ProductListingTaskStatusSchema = z.enum([
  "collecting_source",
  "reviewing_selection",
  "waiting_supplier",
  "waiting_profit",
  "preparing_listing",
  /** 核对上架事实包：草稿生成前必须先把页面/截图/用户三类来源对齐。 */
  "reviewing_facts",
  "awaiting_destination",
  "filling_destination",
  "ready_for_review",
  "completed",
]);
export type ProductListingTaskStatus = z.infer<typeof ProductListingTaskStatusSchema>;

export const ProductListingTaskStepSchema = z.object({
  id: z.string(),
  title: z.string().max(120),
  description: z.string().max(300),
  status: z.enum(["completed", "current", "pending"]),
});
export type ProductListingTaskStep = z.infer<typeof ProductListingTaskStepSchema>;

export const ProductListingFactSchema = z.object({
  label: z.string().max(80),
  value: z.string().max(500),
});
export type ProductListingFact = z.infer<typeof ProductListingFactSchema>;

export { COMPLIANCE_PROMISE_PATTERN, ComplianceCheckKindSchema, ComplianceCheckSchema, ComplianceSeveritySchema, LISTING_COMPLIANCE_DISCLAIMER, ListingAssetSpecSchema, ListingComplianceResultSchema, ListingComplianceRulesSchema, SKU_FACT_IDS, SKU_FACT_TEMPLATE, SKU_FACT_TEMPLATE_BY_ID, SkuFactEntrySchema, SkuFactIdSchema, SkuFactPackSchema } from "./listing-compliance";
export type { ComplianceCheck, ComplianceCheckKind, ComplianceSeverity, ListingComplianceResult, ListingComplianceRules, SkuFactEntry, SkuFactId, SkuFactPack } from "./listing-compliance";
import { ListingComplianceResultSchema, SkuFactEntrySchema, SkuFactIdSchema } from "./listing-compliance";
export {
  LAUNCH_FACT_COPY_EXCLUDED_KEYS, LAUNCH_FACT_HIGH_RISK_KEYS, LAUNCH_FACT_LABELS, LAUNCH_FACT_LENGTH_LIMITS,
  LAUNCH_FACT_REQUIRED_FOR_LISTING, LAUNCH_FACT_VERIFY_KEYS, LAUNCH_NAVIGATION_TERMS, LAUNCH_QUANTITY_UNITS,
  LISTING_TRUSTED_LAUNCH_FACT_SOURCES, LaunchContaminationReasonSchema, LaunchContaminationSchema, LaunchFactKeySchema,
  LaunchFactPackSchema, LaunchFactSchema, LaunchFactSourceSchema, LaunchMinimumOrderQuantitySchema, LaunchMissingFactSchema,
  LaunchSkuFactSchema, LaunchTierPriceSchema, SCREENSHOT_FACT_VALUE_MAX_LENGTH, SCREENSHOT_PAYLOAD_MAX_KEYS,
  SCREENSHOT_REGION_FIELD, SCREENSHOT_REGION_WHOLE_PAGE, applyScreenshotObservation, containsNavigationTerm, deriveMissingLaunchFacts,
  detectLaunchFactContamination, isListingTrustedLaunchFactSource, isTrustedLaunchFact, launchFactConflicts,
  launchFactPackReadiness, listingCopyFacts, MISSING_LAUNCH_FACT_REASON, navigationTermHits, parseMinimumOrderQuantity, parseTierPrices,
  resolvedLaunchFact, resolvedMinimumOrderQuantity, resolvedTierPrices, stripSiteTitleSuffix, trustedLaunchFacts,
} from "./launch-facts";
export type {
  LaunchContamination, LaunchContaminationReason, LaunchFact, LaunchFactKey, LaunchFactPack, LaunchFactPackReadiness,
  LaunchFactSource, LaunchMinimumOrderQuantity, LaunchMissingFact, LaunchSkuFact, LaunchTierPrice,
  LaunchScreenshotObservation, ScreenshotObservationMerge, ScreenshotRegionLabel,
} from "./launch-facts";
import { LaunchFactKeySchema, LaunchFactPackSchema } from "./launch-facts";

export const MarketSignalKindSchema = z.enum(["demand", "competition", "review_pain", "price_band"]);
export type MarketSignalKind = z.infer<typeof MarketSignalKindSchema>;
export const MARKET_SIGNAL_KINDS = MarketSignalKindSchema.options;

/**
 * 市场信号只允许两种状态：有来源，或明确标为"需外部验证"。
 * 没有来源却默认 sourced，会让模型编出来的需求／竞争结论看起来像证据；
 * 反过来，标为需外部验证时也不允许再挂一个来源，避免"先标不确定再偷偷给来源"。
 */
/**
 * §6.6 卡级结论：`sourced` 只说明"有可追溯依据"，`conclusion` 才说明
 * "这条信号到底被观察到了、依据不足、还是根本没核实"。
 * 两者刻意分开：把 `sourced` 直接读成"已核实"正是 §6.6 要防的
 * "有成果 ≠ 已核实"。
 */
export const MarketSignalConclusionSchema = z.enum(["observed", "insufficient", "unverified"]);
export type MarketSignalConclusion = z.infer<typeof MarketSignalConclusionSchema>;

export const MarketSignalSchema = z.object({
  kind: MarketSignalKindSchema,
  finding: z.string().min(1).max(600),
  sourceRef: z.string().min(1).max(300).optional(),
  verification: z.enum(["sourced", "needs_external_verification"]),
  checkedAt: z.string(),
  /**
   * 卡级结论。可选：旧数据与"尚未判定"都落 `undefined`，
   * 不发明一个默认值去顶替"还没结论"。
   */
  conclusion: MarketSignalConclusionSchema.optional(),
  /** 该结论依据的评价条数；与页面已读条数不是同一个量，故单独记。 */
  reviewCount: z.number().int().min(0).max(1_000_000).optional(),
  /** V1 记录：旧客户端照旧读写这一列，形状不变。 */
  reviews: z.array(MarketVerificationRecordSchema).max(20).optional(),
  /**
   * V2 记录单独一列，而不是把 V1 数组改成联合类型（§14）。
   * 原因是旧客户端按 V1 读取 `reviews` 时不应因为多了一种记录就解析失败，
   * 也不该把 V2 的 timeScope 当成 V1 的起止日期。
   */
  reviewsV2: z.array(MarketVerificationRecordV2Schema).max(20).optional(),
}).strict().superRefine((value, context) => {
  if (value.verification === "sourced" && !value.sourceRef) {
    context.addIssue({ code: "custom", path: ["sourceRef"], message: "标为 sourced 的市场信号必须给出来源" });
  }
  if (value.verification === "needs_external_verification" && value.sourceRef) {
    context.addIssue({ code: "custom", path: ["sourceRef"], message: "需外部验证的市场信号不得声明来源" });
  }
  /**
   * §6.6：「拿不出来源只能落 `insufficient`」。
   * `observed` 的含义是"在证据里直接看到了"，没有来源就无法成立；
   * 放在契约里执行，是为了不依赖每个调用方自觉。
   */
  if (value.conclusion === "observed" && !value.sourceRef) {
    context.addIssue({ code: "custom", path: ["conclusion"], message: "没有来源的市场信号不得标记为 observed" });
  }
});
export type MarketSignal = z.infer<typeof MarketSignalSchema>;

/**
 * 评价痛点只能来自用户上传或当前页面已经读到的原文。
 * 没有原文时不产出任何结论：样本为 0 时 clusters 必须为空，只保留说明。
 */
export const ReviewPainQuoteSchema = z.object({ text: z.string().min(1).max(300), sourceRef: z.string().min(1).max(200), sampleIndex: z.number().int().positive().optional() }).strict();
export const ReviewPainClusterSchema = z.object({
  theme: z.string().min(1).max(60),
  mentions: z.number().int().positive(),
  quotes: z.array(ReviewPainQuoteSchema).max(3),
  sellingPointSuggestion: z.string().min(1).max(240),
  sentiment: z.enum(["positive", "negative", "mixed", "unknown"]).optional(),
}).strict().superRefine((value, context) => {
  if (value.mentions < value.quotes.length) context.addIssue({ code: "custom", path: ["mentions"], message: "提及次数不能少于引用条数" });
});
export const ReviewPainSummarySchema = z.object({
  sampleSize: z.number().int().nonnegative(),
  clusters: z.array(ReviewPainClusterSchema).max(8),
  limited: z.boolean(),
  limitation: z.string().min(1).max(300),
}).strict().superRefine((value, context) => {
  if (value.sampleSize === 0 && value.clusters.length > 0) {
    context.addIssue({ code: "custom", path: ["clusters"], message: "没有评价原文时不得产出痛点结论" });
  }
});
export type ReviewPainSummary = z.infer<typeof ReviewPainSummarySchema>;
export type ReviewPainCluster = z.infer<typeof ReviewPainClusterSchema>;

/** 第三方工具的定位：教学路径，不是平台官方规则。 */
export const SelectionToolGuideSchema = z.object({
  id: z.enum(["jijingyun", "sellersprite", "google_trends", "fastmoss", "kalodata", "shoplus", "shopdora", "zhixia", "sorftime_temu", "thunt"]),
  name: z.string().min(1).max(60),
  url: z.string().url().max(600),
  reads: z.string().min(1).max(200),
  steps: z.array(z.string().min(1).max(200)).min(3).max(8),
  decisionRule: z.string().min(1).max(300),
  notOfficial: z.literal(true),
}).strict();
export type SelectionToolGuide = z.infer<typeof SelectionToolGuideSchema>;

/** 候选档案：判断依据 + 结论 + 下一步 + 复核日期。过期后旧结论不再作为当前依据。 */
export const SelectionCandidateSchema = z.object({
  id: z.string().min(1).max(80),
  productTitle: z.string().min(1).max(300),
  sourceUrl: z.string().max(2048).optional(),
  verdict: z.enum(["suitable", "conditional", "insufficient"]),
  basis: z.array(z.string().min(1).max(240)).max(12),
  nextStep: z.string().min(1).max(300),
  reviewedAt: z.string(),
  reviewBy: z.string(),
}).strict();
export type SelectionCandidate = z.infer<typeof SelectionCandidateSchema>;

export const ProductSelectionAssessmentSchema = z.object({
  verdict: z.enum(["suitable", "conditional", "insufficient"]),
  summary: z.string().min(1).max(500),
  strengths: z.array(z.string().min(1).max(240)).max(8).default([]),
  risks: z.array(z.string().min(1).max(240)).max(12).default([]),
  evidence: z.array(ProductListingFactSchema).max(16).default([]),
  /** 需求／竞争／评价痛点／价格带四条市场信号；缺失时留空数组，不假装已核实。 */
  marketSignals: z.array(MarketSignalSchema).max(4).default([]),
  /** 只有真的拿到评价原文时才写入；没有来源时整段缺省。 */
  reviewPain: ReviewPainSummarySchema.optional(),
}).superRefine((value, context) => {
  if (value.verdict === "suitable" && !value.marketSignals.some((signal) => signal.verification === "sourced")) {
    context.addIssue({ code: "custom", path: ["verdict"], message: "没有任何带来源的市场信号时不能判定为适合上架" });
  }
});
export type ProductSelectionAssessment = z.infer<typeof ProductSelectionAssessmentSchema>;

export const SupplierChecklistItemSchema = z.object({
  id: z.string().min(1).max(80),
  question: z.string().min(1).max(240),
  reason: z.string().min(1).max(240),
  status: z.enum(["unknown", "confirmed", "issue"]).default("unknown"),
  note: z.string().max(300).optional(),
});
export type SupplierChecklistItem = z.infer<typeof SupplierChecklistItemSchema>;

export const ProfitInputSchema = z.object({
  currency: z.literal("CNY").default("CNY"),
  sourceUnitCost: z.number().nonnegative().optional(),
  domesticShipping: z.number().nonnegative().default(0),
  internationalShipping: z.number().nonnegative().optional(),
  packagingCost: z.number().nonnegative().default(0),
  otherFixedCost: z.number().nonnegative().default(0),
  sellingPrice: z.number().positive().optional(),
  platformFeeRate: z.number().min(0).max(100).default(0),
  paymentFeeRate: z.number().min(0).max(100).default(0),
  advertisingRate: z.number().min(0).max(100).default(0),
  returnLossRate: z.number().min(0).max(100).default(0),
});
export type ProfitInput = z.infer<typeof ProfitInputSchema>;

export const ProfitResultSchema = z.object({
  complete: z.boolean(),
  revenue: z.number().nonnegative().nullable(),
  fixedCost: z.number().nonnegative(),
  variableCost: z.number().nonnegative().nullable(),
  totalCost: z.number().nonnegative().nullable(),
  profit: z.number().nullable(),
  marginRate: z.number().nullable(),
  breakEvenPrice: z.number().nonnegative().nullable(),
  missingInputs: z.array(z.string().min(1).max(120)).max(12),
  warnings: z.array(z.string().min(1).max(240)).max(12),
});
export type ProfitResult = z.infer<typeof ProfitResultSchema>;

export const ListingDraftSchema = z.object({
  targetPlatform: z.string().min(1).max(80),
  title: z.string().min(1).max(300),
  highlights: z.array(z.string().min(1).max(300)).max(8),
  description: z.string().min(1).max(3000),
  attributes: z.array(ProductListingFactSchema).max(20),
  imageChecklist: z.array(z.string().min(1).max(240)).max(12),
  warnings: z.array(z.string().min(1).max(300)).max(12),
});
export type ListingDraft = z.infer<typeof ListingDraftSchema>;

export const FormApplicationEvidenceSchema = z.object({
  planId: z.string().min(1).max(120),
  status: z.enum(["verified", "partial", "stale"]),
  appliedCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
  mismatchedCount: z.number().int().nonnegative(),
  pageUrl: z.string().max(1200),
  capturedAt: z.string(),
});
export type FormApplicationEvidence = z.infer<typeof FormApplicationEvidenceSchema>;

/**
 * 用户对**真实发布结果**的确认回执。
 *
 * 这张回执是「草稿／已填写」与「已上架」之间唯一的分界：
 * 没有它，任务最多停在 `ready_for_review`，成长路线阶段不通过。
 * `observedState` 记录的是用户在平台里实际看到的状态，不是我们的推断——
 * 所以「只保存了草稿」也能被如实记下来，而不是被当成发布成功。
 */
export const PublishConfirmationSchema = z.object({
  confirmedAt: z.string(),
  observedState: z.enum(["published", "in_review", "draft_saved", "unknown"]),
  platformProductId: z.string().max(120).optional(),
  note: z.string().max(300).optional(),
}).strict();
export type PublishConfirmation = z.infer<typeof PublishConfirmationSchema>;
/** 只有这两种状态证明商品真的离开了草稿箱。 */
export const PUBLISH_CONFIRMATION_PASSING_STATES = ["published", "in_review"] as const;
export const isPublishConfirmationPassing = (confirmation: PublishConfirmation | undefined): boolean =>
  Boolean(confirmation && (PUBLISH_CONFIRMATION_PASSING_STATES as readonly string[]).includes(confirmation.observedState));

export const ProductListingTaskSchema = z.object({
  id: z.string(),
  taskType: z.literal("product_listing"),
  goal: z.string().max(300),
  destinationPlatform: z.string().min(1).max(80),
  status: ProductListingTaskStatusSchema,
  sourcePage: z.object({
    url: z.string().max(1200),
    title: z.string().max(500),
    domain: z.string().max(240),
  }),
  productTitle: z.string().max(300),
  summary: z.string().max(500),
  sourceFacts: z.array(ProductListingFactSchema).max(16),
  missingInformation: z.array(z.string().max(180)).max(12),
  nextAction: z.string().max(300),
  steps: z.array(ProductListingTaskStepSchema).min(3).max(8),
  workflowVersion: z.literal(2).optional(),
  sourcePlatform: z.string().max(80).optional(),
  targetMarket: z.string().max(120).optional(),
  selectionAssessment: ProductSelectionAssessmentSchema.optional(),
  supplierChecklist: z.array(SupplierChecklistItemSchema).max(12).optional(),
  /** 商品问题已生成并随本任务保存；不代表供应商已回复或事实已确认。 */
  supplierQuestionsGeneratedAt: z.string().datetime().optional(),
  profitInput: ProfitInputSchema.optional(),
  profitResult: ProfitResultSchema.optional(),
  listingDraft: ListingDraftSchema.optional(),
  formVerifications: z.array(FormApplicationEvidenceSchema).max(20).optional(),
  pendingFormPlanId: z.string().max(120).optional(),
  /** Current destination requirements, persisted even when source facts cannot produce a plan. */
  listingPageReview: z.object({
    coverage: z.string().max(600).optional(),
    url: z.string().max(1200), title: z.string().max(500), checkedAt: z.string().datetime(),
    fields: z.array(z.object({
      fieldId: z.string(), label: z.string().max(500), required: z.boolean(),
      status: z.enum(["suggested", "present", "missing", "manual", "unrecognized"]),
      options: z.array(z.string().max(500)).max(100).optional(),
      value: z.string().max(10000).optional(), reason: z.string().max(1000),
    })).max(100),
  }).optional(),
  /** Execution is allowed only when id still matches pendingFormPlanId. */
  pendingFormPlan: FormPlanSchema.optional(),
  /** Source candidates matched to the observed destination requirements; never an execution plan. */
  sourceFieldMatches: z.object({
    destinationUrl: z.string().max(1200), requirementsCheckedAt: z.string().datetime(),
    productIdentity: z.string(), sourceUrl: z.string().max(1200), capturedAt: z.string().datetime(),
    fields: z.array(z.object({
      fieldId: z.string(), label: z.string().max(500), required: z.boolean(),
      status: z.enum(["candidate", "inferred", "not_found", "manual", "unavailable", "conflict"]),
      value: z.string().max(2000).optional(), quote: z.string().max(3000).optional(),
      inference: z.object({ kind: z.enum(["translation", "reasoning"]), rationale: z.string().min(1).max(1000), confidence: z.enum(["high", "medium", "low"]) }).optional(),
      evidence: z.array(z.object({ value: z.string().max(2000), quote: z.string().max(3000), source: z.string().max(100) })).max(24).optional(),
    })).max(100),
  }).optional(),
  /** Image-derived dimensions and SKU row relations, not confirmed publishing values. */
  sourceVisualDetails: SourceVisualDetailsSchema.optional(),
  /** SKU 事实包十项；缺少时上架前预检直接判为缺项。 */
  skuFacts: z.array(SkuFactEntrySchema).max(10).optional(),
  /** 最近一次上架前合规预检结果；只读展示，不参与门禁计算。 */
  compliance: ListingComplianceResultSchema.optional(),
  /** 选品候选档案；复核日期过后旧结论不再作为当前依据。 */
  selectionCandidate: SelectionCandidateSchema.optional(),
  /**
   * P2-1（A 方案：**服务端权威**）：这条任务的目标/阶段/子任务树。
   *
   * 由服务端在建任务时生成一次并随任务持久化，客户端只读不自己派生——
   * 两边各算一遍的话，只要有一处输入不同步就会静默分叉（那正是"两处各说一套"的老病）。
   *
   * 可选：旧记录没有树照旧可读，是纯增量改动，**不需要迁移**。
   */
  goalTasks: z.array(GoalTaskSchema).max(40).optional(),
  /**
   * P2-1：四维市场信号 → 子任务节点的映射（服务端生成时一并给出）。
   *
   * 为什么不让客户端自己按标题或交付物字段去猜：猜的规则一旦与拆解定义漂移，
   * 结果**没有任何报错**——要么静默不标注，要么标到错的节点上，把证据挂错子任务。
   * 由生成树的那一方同时给出映射，两边就不可能不一致。
   */
  signalSubtaskNodes: z.partialRecord(MarketSignalKindSchema, z.string().min(1).max(160)).optional(),
  /**
   * 独立上架事实包（选品到上架链路）。
   *
   * 与 `sourceFacts` 并存且互不改写：`sourceFacts` 是前三部分的历史输入，
   * 可能是污染的；**Listing 和填写计划只能消费这个事实包**。
   * 旧记录没有它是正常的——但那也意味着它们的草稿需要重新核对事实后重新生成。
   */
  launchFacts: LaunchFactPackSchema.optional(),
  /**
   * 服务端要求客户端截取**当前整页**再送一轮（不框选、不增加区域选择步骤）。
   *
   * 只有服务端算得出「页面结构读取失败或字段被污染」，所以这个信号由服务端置位、
   * 由客户端消费；两边各算一遍迟早会出现「一边要截图、一边说干净」。
   * 有未裁决冲突时**不**置位：那种情况该由用户裁决，不是再拍一张。
   */
  launchFactsScreenshotRequested: z.boolean().optional(),
  /**
   * 当前这份事实包上已经拍过几轮分段截图。
   *
   * 存在的理由：截图兜底只能补"页面结构读不到"的字段，补不了"必须用户确认"的高风险项
   * （材质、品牌、认证、合规）。只看 `pendingReview` 是否非空就会让这个信号**永远为真**——
   * 用户每点一次"生成草稿"就被重新滚一遍页面拍一轮，而结果永远不会变。
   * 所以按"拍过几轮 + 每轮有没有真的添新事实"收口：有新增才值得再拍，
   * 没新增就停在这里，把剩下的交给用户确认（`review_launch_facts`）。
   */
  launchFactsScreenshotRounds: z.number().int().min(0).max(20).optional(),
  /** 用户对真实发布结果的确认落款；没有它任务不得标记为已上架。 */
  publishConfirmation: PublishConfirmationSchema.optional(),
  /**
   * 当前 Listing 草稿是从哪一个事实包生成的（记录该包的 `capturedAt`）。
   *
   * 这是"这份草稿还能不能继续用于填写"的唯一判据：没有这个绑定、
   * 或它和现存事实包的 `capturedAt` 不一致，草稿就是历史污染草稿，
   * 必须重新核对事实包并重新生成。
   */
  listingDraftFactsCapturedAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProductListingTask = z.infer<typeof ProductListingTaskSchema>;

export const CoachTaskStatusSchema = z.enum([
  "not_started",
  "in_progress",
  "waiting_user",
  "waiting_external",
  "needs_review",
  "completed",
]);
export type CoachTaskStatus = z.infer<typeof CoachTaskStatusSchema>;

export const CoachTaskStepStatusSchema = z.enum(["completed", "current", "pending", "waiting_user", "waiting_external"]);
export type CoachTaskStepStatus = z.infer<typeof CoachTaskStepStatusSchema>;

export const CoachTaskStepSchema = z.object({
  id: z.string().min(1).max(80),
  title: z.string().min(1).max(120),
  description: z.string().min(1).max(300),
  status: CoachTaskStepStatusSchema,
  completionCriteria: z.array(z.string().min(1).max(200)).min(1).max(6),
  caution: z.string().max(300).optional(),
  requiresUserAction: z.boolean().default(false),
});
export type CoachTaskStep = z.infer<typeof CoachTaskStepSchema>;

export const CoachTaskEvidenceSchema = z.object({
  id: z.string(),
  stepId: z.string().min(1).max(80),
  type: z.enum(["current_page", "user_confirmation"]),
  summary: z.string().min(1).max(300),
  pageUrl: z.string().max(1200).optional(),
  capturedAt: z.string(),
});
export type CoachTaskEvidence = z.infer<typeof CoachTaskEvidenceSchema>;

export const CoachTaskPrerequisiteSchema = z.object({
  id: z.string().min(1).max(80),
  label: z.string().min(1).max(160),
  status: z.enum(["confirmed", "missing", "unknown"]),
});
export type CoachTaskPrerequisite = z.infer<typeof CoachTaskPrerequisiteSchema>;

export const PlatformMarketCodeSchema = z.enum(["US", "UK", "SEA", "EU", "JP", "MX"]);
export type PlatformMarketCode = z.infer<typeof PlatformMarketCodeSchema>;

export const OfficialRuleSourceSchema = z.object({
  id: z.string().min(1).max(100),
  title: z.string().min(1).max(240),
  url: z.string().url().max(1200),
  scope: z.string().min(1).max(240),
  publishedAt: z.string().optional(),
  reviewedAt: z.string(),
});
export type OfficialRuleSource = z.infer<typeof OfficialRuleSourceSchema>;

export const CoachTaskRuleContextSchema = z.object({
  rulePackId: z.string().min(1).max(120),
  applicantRoute: z.enum(["cross_border_cn_hk", "local_market"]),
  applicantScope: z.string().min(1).max(240),
  marketCode: PlatformMarketCodeSchema.optional(),
  marketLabel: z.string().min(1).max(100),
  status: z.enum(["verified", "needs_market", "needs_review", "unsupported"]),
  reviewedAt: z.string(),
  nextReviewAt: z.string(),
  summary: z.string().min(1).max(500),
  preparationItems: z.array(z.string().min(1).max(240)).max(12),
  blockingIssues: z.array(z.string().min(1).max(300)).max(6).default([]),
  notices: z.array(z.string().min(1).max(300)).max(8),
  sources: z.array(OfficialRuleSourceSchema).max(8),
});
export type CoachTaskRuleContext = z.infer<typeof CoachTaskRuleContextSchema>;

export const RegistrationPlatformSchema = z.enum(["TikTok Shop", "Temu", "Shopee"]);
export type RegistrationPlatform = z.infer<typeof RegistrationPlatformSchema>;

export const PlatformRegistrationTaskSchema = z.object({
  id: z.string(),
  taskType: z.literal("platform_registration"),
  platform: RegistrationPlatformSchema,
  title: z.string().min(1).max(180),
  goal: z.string().min(1).max(300),
  status: CoachTaskStatusSchema,
  currentStepId: z.string().min(1).max(80),
  summary: z.string().min(1).max(500),
  prerequisites: z.array(CoachTaskPrerequisiteSchema).max(12),
  missingInformation: z.array(z.string().min(1).max(180)).max(12),
  steps: z.array(CoachTaskStepSchema).min(5).max(8),
  evidence: z.array(CoachTaskEvidenceSchema).max(40),
  ruleContext: CoachTaskRuleContextSchema.optional(),
  lastPage: z.object({
    url: z.string().max(1200),
    title: z.string().max(500),
    domain: z.string().max(240),
  }).optional(),
  nextAction: z.string().min(1).max(500),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PlatformRegistrationTask = z.infer<typeof PlatformRegistrationTaskSchema>;
export const TikTokRegistrationTaskSchema = PlatformRegistrationTaskSchema;
export type TikTokRegistrationTask = PlatformRegistrationTask;

export const ActiveCoachTaskSchema = z.discriminatedUnion("taskType", [
  ProductListingTaskSchema,
  PlatformRegistrationTaskSchema,
]);
export type ActiveCoachTask = z.infer<typeof ActiveCoachTaskSchema>;

export const CoachTaskLifecycleSchema = z.enum(["active", "completed", "archived"]);
export type CoachTaskLifecycle = z.infer<typeof CoachTaskLifecycleSchema>;

export const CoachTaskArtifactSchema = z.object({
  id: z.string().min(1).max(100),
  kind: z.enum(["analysis", "checklist", "calculation", "listing_draft", "link", "note"]),
  title: z.string().min(1).max(180),
  summary: z.string().min(1).max(1000),
  data: z.record(z.string(), z.unknown()).default({}),
  createdAt: z.string(),
});
export type CoachTaskArtifact = z.infer<typeof CoachTaskArtifactSchema>;

export const CoachTaskResultSchema = z.object({
  summary: z.string().min(1).max(1000),
  artifacts: z.array(CoachTaskArtifactSchema).max(30).default([]),
  review: z.object({
    outcome: z.enum(["passed", "partial", "blocked"]),
    notes: z.array(z.string().min(1).max(300)).max(12).default([]),
  }).optional(),
});
export type CoachTaskResult = z.infer<typeof CoachTaskResultSchema>;

export const CoachTaskRecordSchema = z.object({
  id: z.string().min(1).max(120),
  lifecycle: CoachTaskLifecycleSchema,
  conversationId: z.string().max(120).nullable(),
  task: ActiveCoachTaskSchema,
  result: CoachTaskResultSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
});
export type CoachTaskRecord = z.infer<typeof CoachTaskRecordSchema>;

export const SaveCoachTaskRequestSchema = z.object({
  conversationId: z.string().max(120).optional(),
  task: ActiveCoachTaskSchema,
});
export type SaveCoachTaskRequest = z.infer<typeof SaveCoachTaskRequestSchema>;

export const UpdateCoachTaskLifecycleRequestSchema = z.object({
  lifecycle: z.enum(["completed", "archived"]),
  result: CoachTaskResultSchema.optional(),
});
export type UpdateCoachTaskLifecycleRequest = z.infer<typeof UpdateCoachTaskLifecycleRequestSchema>;

export const CoachTaskListResponseSchema = z.object({
  tasks: z.array(CoachTaskRecordSchema),
});
export type CoachTaskListResponse = z.infer<typeof CoachTaskListResponseSchema>;

export const CoachTaskCommandSchema = z.enum([
  "start_tiktok_registration",
  "start_temu_registration",
  "start_shopee_registration",
  "start_product_launch",
  "continue",
  "confirm_current_step",
  "review_supplier",
  "calculate_profit",
  "generate_listing_draft",
  /**
   * 站在**货源页**重读商品事实并重新生成草稿。
   *
   * 为什么需要单独一条：任务一旦走到"去 Temu 填写"（`awaiting_destination` /
   * `filling_destination`），卡片上的按钮只剩"读取发布页"，用户**没有任何入口**回到 1688
   * 重新采集事实包。真机 2026-09-22：事实包停在"只有标题"，滚动加载/分段截图这条新路
   * 因此永远触发不了。这条命令就是那个入口。
   */
  "reread_source_page",
  "review_launch_facts",
  "confirm_sku_facts",
  "record_selection_reviews",
  "verify_market_evidence",
  "verify_form_application",
  "resume_after_mobile",
  "confirm_external_success",
]);
export type CoachTaskCommand = z.infer<typeof CoachTaskCommandSchema>;

export const CoachTaskInputSchema = z.discriminatedUnion("type", [
  MarketEvidenceSubmissionSchema,
  // V2 与 V1 的判别式不同（market_evidence_v2），旧输入形状保持不变。
  MarketEvidenceSubmissionV2Schema,
  z.object({
    type: z.literal("supplier_review"),
    confirmedItemIds: z.array(z.string().min(1).max(80)).max(12),
    issueItemIds: z.array(z.string().min(1).max(80)).max(12).default([]),
  }),
  z.object({
    type: z.literal("profit"),
    values: ProfitInputSchema,
  }),
  z.object({
    type: z.literal("sku_facts"),
    /** 用户逐项确认的十项事实包：confirmed 表示已核对，needsReview 表示需要人工判断。 */
    confirmedItemIds: z.array(SkuFactIdSchema).max(10).default([]),
    needsReviewItemIds: z.array(SkuFactIdSchema).max(10).default([]),
    owner: z.string().min(1).max(80),
  }),
  z.object({
    type: z.literal("selection_reviews"),
    /**
     * 评价痛点原文。来源只允许"用户上传"与"当前页可读"两种，
     * 且由服务端按 origin 拼 sourceRef——客户端不能自带来源前缀，否则凭据可以伪造。
     */
    origin: z.enum(["user_upload", "current_page"]),
    /** 出处标识：上传时是文件名或"用户粘贴"，读页面时是当前页地址。 */
    reference: z.string().min(1).max(160),
    samples: z.array(z.string().min(1).max(1200)).min(1).max(200),
  }),
  z.object({
    type: z.literal("form_application"),
    planId: z.string().min(1).max(120),
    status: z.enum(["verified", "partial", "stale"]),
    appliedFieldIds: z.array(z.string().min(1).max(120)).max(100),
    skippedFieldIds: z.array(z.string().min(1).max(120)).max(100),
    mismatchedFieldIds: z.array(z.string().min(1).max(120)).max(100),
    pageUrl: z.string().max(1200),
  }),
  /**
   * 用户对真实发布结果的确认。`confirmedAt` 由服务端写入，
   * 客户端只能提交自己实际看到的状态——包括"只是草稿"。
   */
  z.object({
    type: z.literal("publish_confirmation"),
    observedState: z.enum(["published", "in_review", "draft_saved", "unknown"]),
    platformProductId: z.string().max(120).optional(),
    note: z.string().max(300).optional(),
  }).strict(),
  /**
   * 用户核对上架事实包。按 (key, value) 逐项确认而不是只按 key：
   * 页面与截图给出不同值时，用户选的**是哪一个值**才构成裁决。
   * `needsReview` 把项降级为待核对，不允许顺手确认一个没读到的值。
   */
  z.object({
    type: z.literal("launch_facts"),
    confirmed: z.array(z.object({ key: LaunchFactKeySchema, value: z.string().min(1).max(600) })).max(40).default([]),
    needsReview: z.array(z.object({ key: LaunchFactKeySchema, value: z.string().min(1).max(600) })).max(40).default([]),
  }).strict(),
]);
export type CoachTaskInput = z.infer<typeof CoachTaskInputSchema>;

export const AgentRouteSchema = z.enum(["text", "search", "vision", "page", "form", "action"]);
export type AgentRoute = z.infer<typeof AgentRouteSchema>;

export const IntentTaskSchema = z.enum([
  "consultation",
  "how_to",
  "latest_information",
  "page_question",
  "form_help",
  "image_question",
  "product_selection",
  "product_listing",
  "platform_registration",
  "browser_action",
]);
export type IntentTask = z.infer<typeof IntentTaskSchema>;

export const ContextSourceSchema = z.enum(["none", "conversation", "current_page", "both"]);
export type ContextSource = z.infer<typeof ContextSourceSchema>;

export const SearchDepthSchema = z.enum(["fast", "deep"]);
export type SearchDepth = z.infer<typeof SearchDepthSchema>;

export const SiteTypeSchema = z.enum(["commerce", "general"]);
export type SiteType = z.infer<typeof SiteTypeSchema>;

export const BrowserActionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("marketplace_search"),
    platform: z.string().min(1).max(60),
    query: z.string().min(1).max(160),
    searchLocalization: SearchLocalizationSchema.optional(),
  }),
  z.object({
    type: z.literal("open_platform"),
    platform: z.string().min(1).max(60),
  }),
  /**
   * 打开一个**显式 URL**，不需要站点事先登记。
   *
   * 前两个变体都要靠 `SITE_REGISTRY` 才能构造出目的地，所以"未登记"在它们那里
   * 只能被拒绝；`open_url` 是"新平台不用改代码"的入口。
   * 安全性由 URL 公开性判定 + 既有的逐段用户确认共同保证（见 `public-url.ts`）。
   */
  z.object({
    type: z.literal("open_url"),
    url: PublicNavigationUrlSchema,
  }),
]);
export type BrowserAction = z.infer<typeof BrowserActionSchema>;

export const IntentDecisionSchema = z.object({
  route: AgentRouteSchema,
  taskType: IntentTaskSchema,
  contextSource: ContextSourceSchema,
  needsPageContext: z.boolean(),
  needsOfficialSearch: z.boolean(),
  needsKnowledgeBase: z.boolean().default(false),
  searchDepth: SearchDepthSchema,
  confidence: z.enum(["high", "medium", "low"]),
  reason: z.string().max(240),
  targetPlatform: z.string().max(80).optional(),
  currentPageRole: z.enum(["none", "source", "destination", "unknown"]).optional(),
  browserAction: BrowserActionSchema.optional(),
  scope: z.lazy(() => CoachingScopeSchema).optional(),
}).superRefine((value, context) => {
  const expectsPage = value.contextSource === "current_page" || value.contextSource === "both";
  if (value.needsPageContext !== expectsPage) {
    context.addIssue({ code: "custom", path: ["needsPageContext"], message: "必须与 contextSource 保持一致" });
  }
});
export type IntentDecision = z.infer<typeof IntentDecisionSchema>;

export const ContextEvidenceRequestSchema = z.object({
  message: z.string().min(1).max(8000),
  context: PageContextSchema.optional(),
  history: z.array(ConversationTurnSchema).max(12),
  intent: IntentDecisionSchema,
  hasImages: z.boolean(),
}).strict();
export type ContextEvidenceRequest = z.infer<typeof ContextEvidenceRequestSchema>;

export const ContextEvidenceDecisionSchema = z.object({
  decision: z.enum(["answer", "screenshot", "clarify"]),
  reason: z.string().min(1).max(240),
}).strict();
export type ContextEvidenceDecision = z.infer<typeof ContextEvidenceDecisionSchema>;

/** Status only: image bytes travel exclusively through the existing images field. */
export * from "./screenshot-region";
import { ScreenshotRegionSchema } from "./screenshot-region";
export const PageScreenshotSchema = z.object({
  status: z.enum(["attached", "declined", "unavailable"]),
  /**
   * 覆盖情况的一句话说明。
   *
   * 额度从 240 提到 600：真机 2026-09-23 因为这句话超长，**整个请求被校验拒绝**
   * （`pageScreenshot.reason too_big`），用户只看到"参数未通过校验"。
   * 让"说明太长"演变成"功能不可用"是最糟的一种失败。
   */
  reason: z.string().max(600).optional(),
  region: ScreenshotRegionSchema.optional(),
  /**
   * 分段截图的覆盖情况（1688 上架事实包兜底）。
   *
   * 存在的理由：分段截图**天然拍不全**超长页面（`captureVisibleTab` 一次只有一屏），
   * 而"没拍到"与"页面上没有"在事实包里后果完全不同——前者是未知，后者会被写成
   * "该商品没有材质"。所以覆盖范围与缺口必须随截图一起上报，而不是只报一个 `attached`。
   */
  segments: z.object({
    /**
     * 页面总高（像素）：把"图内归一化坐标"换算成"页内归一化坐标"的分母。
     * 客户端实测上报，服务端不自己推算——页面高度只有拍的那一刻才准。
     */
    pageHeight: z.number().int().min(1).max(10_000_000).optional(),
    /** 实际拍到的段数。 */
    captured: z.number().int().min(1).max(64),
    /** 整页被切成多少段（含没拍到的）。 */
    total: z.number().int().min(1).max(512),
    /** 拍到但没进请求的段数（超出模型一次能收的图片额度）。 */
    omitted: z.number().int().min(0).max(512).default(0),
    /** 完全没拍到的内容高度（像素）。 */
    missedPixels: z.number().int().min(0).max(10_000_000).default(0),
    /**
     * 每一段在页面上的位置与高度（像素），与请求里的图片**按下标一一对应**。
     *
     * 存在的理由：截图上的字段名要按**纵向位置**对到 DOM 字段上
     * （真机 2026-09-22 Temu 发布页：商品名称/详情描述在 DOM 里只有占位符，
     * 只能靠截图上的名字补），而模型给的是"图内归一化位置"。
     * 没有这一段几何就无法把两者换算到同一个坐标系——那就只能不做位置配对，
     * 而不是拿错坐标去猜（错配会把值填进错误的框）。
     *
     * 可选：老客户端不报，服务端就退回纯文字匹配，行为与以前一致。
     */
    geometry: z.array(z.object({
      y: z.number().int().min(0).max(10_000_000),
      height: z.number().int().min(1).max(100_000),
    }).strict()).max(64).optional(),
    /** Each screenshot is paired only with the visible controls read in that viewport. */
    fieldSnapshots: z.array(z.array(ElementSummarySchema).max(100)).max(MAX_IMAGES_PER_RUN).optional(),
    /**
     * 逐段的实际位置（结构化诊断读数）。
     *
     * 真机 2026-09-23：把"实测底部 / 已拍到 / 逐段位置"写进 `pageScreenshot.reason`，
     * 超了它的长度上限，**整个请求被校验拒绝**（`too_big`），用户只看到"参数未通过校验"。
     * 诊断读数该放在有自己的额度、可校验的结构里，而不是挤占说明文字。
     */
    capturedAt: z.array(z.object({
      y: z.number().int().min(0).max(10_000_000),
      height: z.number().int().min(1).max(100_000),
    }).strict()).max(64).optional(),
    /** 实测"这一页能滚到哪"（像素）；与逐段位置一比就知道差多少。 */
    physicalBottom: z.number().int().min(0).max(10_000_000).optional(),
  }).strict().optional(),
}).strict();
export type PageScreenshot = z.infer<typeof PageScreenshotSchema>;

export const PageHintSchema = z.object({
  // Marketplace referral URLs commonly exceed 1200 characters. Keep the full
  // identity (including search / SKU parameters); never truncate executable URLs.
  url: z.string().max(8192).optional(),
  domain: z.string().max(240).optional(),
  title: z.string().max(500).optional(),
  siteType: SiteTypeSchema.optional(),
});
export type PageHint = z.infer<typeof PageHintSchema>;

export const IntentClassificationRequestSchema = z.object({
  conversationId: z.string().min(1).max(160).optional(),
  message: z.string().min(1).max(8000),
  mode: ModeSchema.default("balanced"),
  history: z.array(ConversationTurnSchema).max(12).default([]),
  profile: SellerProfileSchema.optional(),
  forcedTool: ForcedToolSchema,
  hasImages: z.boolean().default(false),
  imageNames: z.array(z.string().max(180)).max(MAX_IMAGES_PER_RUN).default([]),
  selectedElement: ElementSummarySchema.optional(),
  pageHint: PageHintSchema.optional(),
  activeTask: ActiveCoachTaskSchema.optional(),
  taskCommand: CoachTaskCommandSchema.optional(),
  taskInput: CoachTaskInputSchema.optional(),
  coachActive: z.boolean().optional(),
});
export type IntentClassificationRequest = z.infer<typeof IntentClassificationRequestSchema> & { memory?: import("./conversations").ConversationMemory };

export const FollowupQuestionsSchema = z.object({
  questions: z.array(z.string().min(2).max(60)).min(1).max(3),
});
export type FollowupQuestions = z.infer<typeof FollowupQuestionsSchema>;

export * from "./recommendations";
export * from "./recommendation-background";
import { RecommendationContextSchema } from "./recommendations";
export const CoachSuggestionRequestSchema = z.object({
  context: PageContextSchema,
  profile: SellerProfileSchema.optional(),
  activeTask: ActiveCoachTaskSchema.optional(),
  protocolVersion: z.literal(2).optional(),
  conversationId: z.string().min(1).max(160).optional(),
  recommendationContext: RecommendationContextSchema.optional(),
});
export type CoachSuggestionRequest = z.infer<typeof CoachSuggestionRequestSchema>;

export const AgentRunRequestSchema = z.object({
  conversationId: z.string().min(1).max(160),
  messageId: z.string().min(1).max(160).optional(),
  retryOf: z.string().min(1).max(160).optional(),
  message: z.string().min(1).max(8000),
  mode: ModeSchema.default("balanced"),
  context: PageContextSchema.optional(),
  images: z.array(ImageAttachmentSchema).max(MAX_IMAGES_PER_RUN).default([]),
  pageScreenshot: PageScreenshotSchema.optional(),
  formLocationRequested: z.boolean().optional(),
  history: z.array(ConversationTurnSchema).max(12).default([]),
  profile: SellerProfileSchema.optional(),
  siteType: SiteTypeSchema.optional(),
  intent: IntentDecisionSchema.optional(),
  forcedTool: ForcedToolSchema,
  activeTask: ActiveCoachTaskSchema.optional(),
  taskCommand: CoachTaskCommandSchema.optional(),
  taskInput: CoachTaskInputSchema.optional(),
  coachActive: z.boolean().optional(),
  // 客户端只声明"哪一个成长任务的哪一步"；字段目录与已确认值由服务端装配，
  // 不接受客户端提交 definitions/values，避免伪造"这个字段不缺失"。
  growthTaskId: z.string().uuid().optional(),
  growthStepPosition: z.number().int().min(1).max(3).optional(),
});
// Server-only background: external request schema strips untrusted memory fields.
// growthTask 同样只由服务端装配（见 app.ts 的成长任务上下文构建）。
export type AgentRunRequest = z.infer<typeof AgentRunRequestSchema> & {
  memory?: import("./conversations").ConversationMemory;
  growthTask?: import("./task-fields").GrowthTaskContext;
};

export type AgentEvent =
  | { type: "history.status"; data: { saved: boolean; message: string } }
  | { type: "run.started"; data: { runId: string } }
  | { type: "image.analysis"; data: ImageAnalysisEventData }
  | { type: "route.selected"; data: { route: AgentRoute; model: string; taskType: IntentTask } }
  | { type: "progress.updated"; data: { stage: "knowledge" | "search" | "task" | "form" | "answer"; message: string } }
  | { type: "response.delta"; data: { text: string } }
  | { type: "products.selected"; data: ProductCards }
  | { type: "source.added"; data: AnswerSource }
  | { type: "form.plan"; data: FormPlan }
  | { type: "task.plan"; data: ActiveCoachTask }
  | { type: "task.fields.proposed"; data: import("./task-fields").TaskFieldProposal }
  | { type: "browser.action"; data: { action: BrowserAction; summary: string } }
  | { type: "followups.generated"; data: { questions: string[] } }
  | { type: "response.completed"; data: { runId: string; answer: string } }
  | { type: "case.saved"; data: { caseId: string } }
  | { type: "run.failed"; data: { message: string; code: string } };

export type ExtensionMessage =
  | { type: "GET_ACTIVE_TAB" }
  | { type: "ACTIVE_TAB_CHANGED"; payload: { id?: number; url?: string; title?: string; favIconUrl?: string; reason: "activated" | "updated"; coachingExpected?: boolean } }
  | { type: "GET_PAGE_CONTEXT" }
  | { type: "GET_RECOMMENDATION_SIGNATURE" }
  /** 读取当前页上结构化的 1–3 星评价原文（评价痛点只吃页面真的写了的内容）。 */
  | { type: "READ_PAGE_REVIEWS" }
  | { type: "PREPARE_REVIEW_COLLECTION" }
  | { type: "COLLECT_DOCUMENT_REVIEWS"; documentKey: string }
  /**
   * 把评价区滚进视野（评论那条的"点评论区块"）。
   * **只读**：只按"声明了 `aria-controls` 且指向文档内面板"的判定认那个评价视图，
   * 不点提交/外链按钮；找不到就如实回报。
   */
  | { type: "SCROLL_TO_REVIEWS" }
  | { type: "CAPTURE_SCREENSHOT" }
  | { type: "START_ELEMENT_PICKER"; pickerId?: string }
  | { type: "STOP_ELEMENT_PICKER"; pickerId?: string }
  | { type: "ELEMENT_PICKER_STOPPED"; pickerId?: string; tabId?: number; selectedElement?: ElementSummary }
  | { type: "ELEMENT_SELECTED"; payload: ElementSummary; pickerId?: string }
  | { type: "APPLY_FORM_VALUES"; payload: FormApplicationRequest }
  | { type: "UNDO_FORM_VALUES"; payload?: { planId: string; pageUrl: string } }
  | { type: "SET_COACH_ACTIVE"; payload: { active: boolean } }
  | { type: "EXECUTE_BROWSER_ACTION"; payload: BrowserAction }
  | { type: "RECOMMENDATION_PAGE_CHANGED"; payload: { url: string; signature: string; known: boolean } }
  | { type: "PAGE_CHANGED"; payload: { url: string; title: string } };

export const PhoneSchema = z.string().regex(/^\+?[1-9]\d{7,14}$/, "手机号格式不正确");
export const SmsPurposeSchema = z.enum(["login", "register", "reset_password", "bind_phone"]);
export type SmsPurpose = z.infer<typeof SmsPurposeSchema>;

export const SendSmsRequestSchema = z.object({
  phone: PhoneSchema,
  purpose: SmsPurposeSchema,
});
export type SendSmsRequest = z.infer<typeof SendSmsRequestSchema>;

export const SmsLoginRequestSchema = z.object({
  phone: PhoneSchema,
  code: z.string().regex(/^\d{4,8}$/),
  purpose: SmsPurposeSchema.default("login"),
});
export type SmsLoginRequest = z.infer<typeof SmsLoginRequestSchema>;

export const PasswordLoginRequestSchema = z.object({
  phone: PhoneSchema,
  password: z.string().min(8).max(128),
});
export type PasswordLoginRequest = z.infer<typeof PasswordLoginRequestSchema>;

export const SetPasswordRequestSchema = z.object({
  password: z.string().min(8).max(128),
  smsCode: z.string().regex(/^\d{4,8}$/).optional(),
});
export type SetPasswordRequest = z.infer<typeof SetPasswordRequestSchema>;

export const AuthUserSchema = z.object({
  id: z.string().uuid(),
  role: z.enum(["user", "admin"]),
  displayName: z.string().nullable(),
  phone: z.string().nullable(),
  wechatBound: z.boolean(),
  status: z.enum(["active", "disabled"]),
});
export type AuthUser = z.infer<typeof AuthUserSchema>;

export const AuthTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  accessExpiresAt: z.string(),
  refreshExpiresAt: z.string(),
});
export type AuthTokens = z.infer<typeof AuthTokensSchema>;

export const AuthSessionResponseSchema = z.object({
  sessionFamilyId: z.string().uuid().optional(),
  user: AuthUserSchema,
  tokens: AuthTokensSchema,
});
export type AuthSessionResponse = z.infer<typeof AuthSessionResponseSchema>;

export const RefreshSessionRequestSchema = z.object({ refreshToken: z.string().min(20) });
export type RefreshSessionRequest = z.infer<typeof RefreshSessionRequestSchema>;

export const WechatLoginStartResponseSchema = z.object({
  authorizationUrl: z.string().url(),
  state: z.string(),
});
export type WechatLoginStartResponse = z.infer<typeof WechatLoginStartResponseSchema>;

export const PlanCodeSchema = z.enum(["basic", "plus", "pro"]);
export type PlanCode = z.infer<typeof PlanCodeSchema>;

export const PlanSchema = z.object({
  code: PlanCodeSchema,
  name: z.string(),
  description: z.string(),
  durationDays: z.number().int().positive(),
  dailyGrantPoints: z.number().int().nonnegative(),
  featureFlags: z.record(z.string(), z.boolean()).default({}),
  active: z.boolean(),
});
export type Plan = z.infer<typeof PlanSchema>;

export const ProductKindSchema = z.enum(["ai_plan", "point_pack", "human_support"]);
export type ProductKind = z.infer<typeof ProductKindSchema>;

export const ProductSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string(),
  kind: ProductKindSchema,
  planCode: PlanCodeSchema.nullable(),
  priceCents: z.number().int().nonnegative(),
  currency: z.literal("CNY"),
  grantPoints: z.number().int().nonnegative(),
  active: z.boolean(),
});
export type Product = z.infer<typeof ProductSchema>;

export const OrderStatusSchema = z.enum([
  "pending_payment",
  "paid",
  "fulfilled",
  "closed",
  "refunding",
  "refunded",
  "partially_refunded",
]);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

export const PaymentProviderSchema = z.enum(["wechat", "alipay", "mock"]);
export type PaymentProviderName = z.infer<typeof PaymentProviderSchema>;

export const CreateOrderRequestSchema = z.object({ productCode: z.string().min(1).max(80) });
export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;

export const OrderSchema = z.object({
  id: z.string().uuid(),
  orderNo: z.string(),
  productCode: z.string(),
  productName: z.string(),
  kind: ProductKindSchema,
  amountCents: z.number().int().nonnegative(),
  currency: z.literal("CNY"),
  status: OrderStatusSchema,
  createdAt: z.string(),
  paidAt: z.string().nullable(),
});
export type Order = z.infer<typeof OrderSchema>;

export const CreatePaymentRequestSchema = z.object({
  provider: PaymentProviderSchema,
});
export type CreatePaymentRequest = z.infer<typeof CreatePaymentRequestSchema>;

export const PaymentActionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("qr_code"), content: z.string(), expiresAt: z.string().optional() }),
  z.object({ type: z.literal("redirect"), url: z.string().url() }),
  z.object({ type: z.literal("html_form"), html: z.string() }),
  z.object({ type: z.literal("mock"), paymentNo: z.string() }),
]);
export type PaymentAction = z.infer<typeof PaymentActionSchema>;

export const CreatePaymentResponseSchema = z.object({
  paymentNo: z.string(),
  orderNo: z.string(),
  provider: PaymentProviderSchema,
  action: PaymentActionSchema,
});
export type CreatePaymentResponse = z.infer<typeof CreatePaymentResponseSchema>;

export const AccountSummarySchema = z.object({
  user: AuthUserSchema,
  plan: PlanSchema.nullable(),
  planEndsAt: z.string().nullable(),
  pointsBalance: z.number().int().nonnegative(),
  dailyPointsGranted: z.number().int().nonnegative(),
});
export type AccountSummary = z.infer<typeof AccountSummarySchema>;

export const PointLedgerEntrySchema = z.object({
  id: z.string(),
  change: z.number().int(),
  balanceAfter: z.number().int().nonnegative(),
  reason: z.enum(["daily_grant", "purchase", "consume", "release", "refund", "adjustment", "expire"]),
  createdAt: z.string(),
  route: AgentRouteSchema.optional(),
  taskType: IntentTaskSchema.optional(),
});
export type PointLedgerEntry = z.infer<typeof PointLedgerEntrySchema>;

export const PointCostRuleSchema = z.object({
  route: AgentRouteSchema,
  name: z.string(),
  points: z.number().int().positive(),
  description: z.string(),
});
export type PointCostRule = z.infer<typeof PointCostRuleSchema>;

export const PointRulesResponseSchema = z.object({
  operations: z.array(PointCostRuleSchema),
  deepModeExtra: z.number().int().nonnegative(),
  timezone: z.literal("Asia/Shanghai"),
  dailyPointsExpireAtMidnight: z.literal(true),
  purchasedPointsUsedAfterDaily: z.literal(true),
  failedRunsRefunded: z.literal(true),
});
export type PointRulesResponse = z.infer<typeof PointRulesResponseSchema>;

export const CreateRefundRequestSchema = z.object({
  amountCents: z.number().int().positive(),
  reason: z.string().min(2).max(240),
});
export type CreateRefundRequest = z.infer<typeof CreateRefundRequestSchema>;
export * from "./extension-connection";
export * from "./user-feedback";
export * from "./selection-support";
export * from "./product-identity";
export * from "./source-field-candidates";
